import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';

import { Box, Stack } from '@mui/system';
import {
  Card,
  Checkbox,
  useTheme,
  CardHeader,
  Typography,
  CardContent,
  FormHelperText,
  FormControlLabel
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { OrganizationSetting } from 'src/services/organisation/organisation.types';
import {
  useGetOrganizationSettingsQuery,
  useUpdateOrganizationSettingsMutation
} from 'src/services/organisation/organisation.service';

import Iconify from 'src/components/iconify';

export default function SilentRejectSetting() {

  const { t } = useTranslate();
  const theme = useTheme();

  const { organization, account } = useAuthContext();

  const { currentData: settingsData } = useGetOrganizationSettingsQuery(
    organization.id
  );

  const [updateSettings] = useUpdateOrganizationSettingsMutation();

  const [silentRejectEnabled, setSilentRejectEnabled] = useState(false);

  useEffect(() => {
    if (settingsData) {

      const silentRejectionSetting = settingsData.find(
        (setting) => setting.setting_type === OrganizationSetting.SILENT_REJECTION_ENABLED
      );

      if (silentRejectionSetting) {
        setSilentRejectEnabled(silentRejectionSetting.setting_value as boolean);
      }

    }
  }, [settingsData, setSilentRejectEnabled]);

  const onRejectionSettingsUpdate = async (value: boolean) => {

    setSilentRejectEnabled(value);

    try {
      const settingsToUpdate = [];

      if (!settingsData) return;

      const silentRejectEnabledSetting = settingsData.find(
        (setting) => setting.setting_type === 'silent_rejection_enabled'
      );

      if (silentRejectEnabledSetting) {
        settingsToUpdate.push({
          id: silentRejectEnabledSetting.id,
          value
        });
      }

      if (settingsToUpdate.length === 0) return;

      await updateSettings({
        organizationId: organization.id,
        settings: settingsToUpdate,
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.business_settings.update.success'), { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(t('admin-organization.settings.business_settings.update.default_error'), { variant: 'error' });
    }
  }

  const canEditSettings = useMemo(() => {
    if (!account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(account.org_role);
  }, [account]);

  return (
    <Box sx={{ width: '100%' }}>
      <Card
        sx={{
          width: '100%',
          position: 'relative',
          boxShadow: 0
        }}
      >
        <CardHeader
          avatar={
            <Iconify
              icon="hugeicons:notification-block-01"
              sx={{
                width: 30,
                height: 30,
                color: theme.palette.primary.main,
              }}
            />
          }
          title={
            <Typography variant="h6" lineHeight={1.75}>
              {t('admin-organization.settings.silent_reject.title')}
            </Typography>
          }
        />

        <CardContent>

          <Stack direction="column" justifyContent="center" sx={{ mt: 2 }} gap={2}>

            <FormControlLabel
              disabled={!canEditSettings}
              label={
                <Stack gap={0} sx={{ ml: 1 }}>
                  <Typography variant="body2">
                    {t('admin-organization.settings.silent_reject.description')}
                  </Typography>
                  <FormHelperText sx={{ mt: 0 }}>
                    {t('admin-organization.settings.silent_reject.helper')}
                  </FormHelperText>
                </Stack>
              }
              control={<Checkbox checked={silentRejectEnabled} onChange={(e) => onRejectionSettingsUpdate(e.target.checked)} />}
            />

          </Stack>

        </CardContent>
      </Card>
    </Box>
  )
}